import { z } from 'zod';

export const DOWNGRADE_NOTES_MAX_LENGTH = 500;

export const ARCHIVAL_REASON_OPTIONS = [
  { value: 'billing_pricing', label: 'Billing / pricing' },
  { value: 'onboarding_friction', label: 'Onboarding friction' },
  { value: 'product_fit', label: 'Product fit' },
  { value: 'data_accuracy_trust', label: 'Data accuracy / trust' },
  { value: 'technical_setup_issues', label: 'Technical setup issues' },
  { value: 'responsiveness_support', label: 'Responsiveness / support experience' },
  { value: 'internal_changes', label: 'Internal changes' },
  { value: 'timing_readiness', label: 'Timing / readiness' },
  { value: 'moved_to_competitor', label: 'Moved to a competitor or alternative' },
  { value: 'no_longer_needed', label: 'No longer needed' },
  { value: 'other', label: 'Other' },
] as const;

export const archivalReasonSchema = z.enum([
  'billing_pricing',
  'onboarding_friction',
  'product_fit',
  'data_accuracy_trust',
  'technical_setup_issues',
  'responsiveness_support',
  'internal_changes',
  'timing_readiness',
  'moved_to_competitor',
  'no_longer_needed',
  'other',
]);

export type ArchivalReason = z.infer<typeof archivalReasonSchema>;

/** Wire values of `DowngradeTargetPlan`; Starter is `GROWTH`. */
export const downgradeTargetPlanSchema = z.enum(['GROWTH', 'FREE']);

export type DowngradeTargetPlan = z.infer<typeof downgradeTargetPlanSchema>;

export const DOWNGRADE_REASON_REQUIRED_MESSAGE = 'Please select a reason to continue.';
export const DOWNGRADE_NOTES_REQUIRED_MESSAGE = 'Please add a note when selecting "Other."';
export const DOWNGRADE_PLAN_REQUIRED_MESSAGE = 'Please select a plan to continue.';

/** Step 1 form. Reason and notes are only validated while Free + archive is chosen. */
export const downgradeFormSchema = z
  .object({
    target_plan: downgradeTargetPlanSchema.nullable(),
    archive: z.boolean(),
    archival_reason: archivalReasonSchema.nullable(),
    archival_reason_notes: z
      .string()
      .max(DOWNGRADE_NOTES_MAX_LENGTH, `Notes can be at most ${DOWNGRADE_NOTES_MAX_LENGTH} characters.`),
  })
  .superRefine((values, ctx) => {
    if (values.target_plan === null) {
      ctx.addIssue({ code: 'custom', path: ['target_plan'], message: DOWNGRADE_PLAN_REQUIRED_MESSAGE });
      return;
    }
    if (values.target_plan !== 'FREE' || !values.archive) {
      return;
    }
    if (values.archival_reason === null) {
      ctx.addIssue({ code: 'custom', path: ['archival_reason'], message: DOWNGRADE_REASON_REQUIRED_MESSAGE });
      return;
    }
    if (values.archival_reason === 'other' && values.archival_reason_notes.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['archival_reason_notes'], message: DOWNGRADE_NOTES_REQUIRED_MESSAGE });
    }
  });

export type DowngradeFormValues = z.infer<typeof downgradeFormSchema>;

/** POST body of `/admin/organizations/{id}/downgrade` (`DowngradeRequest`). */
export const downgradeRequestSchema = z.object({
  target_plan: downgradeTargetPlanSchema,
  archive: z.boolean(),
  archival_reason: archivalReasonSchema.nullable(),
  archival_reason_notes: z.string().max(DOWNGRADE_NOTES_MAX_LENGTH).nullable(),
});

export type DowngradeRequest = z.infer<typeof downgradeRequestSchema>;

/** Response of the downgrade endpoint (`DowngradeResult`). */
export const downgradeResultSchema = z.object({
  organization_id: z.string(),
  from_plan: z.enum(['PREMIUM', 'GROWTH']),
  to_plan: downgradeTargetPlanSchema,
  archive: z.boolean(),
  effective: z.enum(['END_OF_TERM', 'IMMEDIATELY']),
  current_term_end: z.string().nullable(),
  already_applied: z.boolean(),
  chargebee_subscription_id: z.string().nullable(),
});

export type DowngradeResult = z.infer<typeof downgradeResultSchema>;
